import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

export default function RagUpload() {
  const { tenantId } = useTenant();
  const [kbId, setKbId] = useState<string>("");
  const [kbList, setKbList] = useState<Array<{ id: string; nome: string }>>([]);
  const [file, setFile] = useState<File | null>(null);
  const [n8nUrlFiles, setN8nUrlFiles] = useState<string>("");
  const [n8nUrlDrive, setN8nUrlDrive] = useState<string>("");
  const [driveFileId, setDriveFileId] = useState<string>("");
  const [savingWebhooks, setSavingWebhooks] = useState(false);
  const canUse = useMemo(() => !!tenantId, [tenantId]);

  useEffect(() => {
    const presetFiles = (import.meta as any).env?.VITE_N8N_RAG_FILES_URL as string | undefined;
    const presetDrive = (import.meta as any).env?.VITE_N8N_RAG_DRIVE_URL as string | undefined;
    const filesKey = tenantId ? `rag:n8n:files:${tenantId}` : null;
    const driveKey = tenantId ? `rag:n8n:drive:${tenantId}` : null;
    const storedFiles = filesKey ? localStorage.getItem(filesKey) : null;
    const storedDrive = driveKey ? localStorage.getItem(driveKey) : null;
    if (storedFiles) setN8nUrlFiles(storedFiles);
    else if (presetFiles) setN8nUrlFiles(presetFiles);
    if (storedDrive) setN8nUrlDrive(storedDrive);
    else if (presetDrive) setN8nUrlDrive(presetDrive);
    const loadKb = async () => {
      if (!tenantId) return;
      const { data } = await supabase.from("knowledge_bases" as any).select("id,nome").eq("tenant_id", tenantId).eq("status", "ativo");
      setKbList((data as any[]) || []);
    };
    loadKb();
  }, [tenantId]);

  const uploadAndIndex = async () => {
    if (!tenantId || !file || !n8nUrlFiles || !kbId) {
      toast("Preencha Tenant, KB, arquivo e URL do n8n");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    const { data, error } = await supabase.functions.invoke("ai-upload-asset", { body: formData } as any);
    if (error || !data?.signed_url) {
      toast("Erro ao enviar arquivo");
      return;
    }
    const payload = {
      file_url: data.signed_url,
      tenant_id: tenantId,
      kb_id: kbId,
      filename: file.name,
      mime_type: file.type,
    };
    const res = await fetch(n8nUrlFiles, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) {
      toast("Erro ao acionar ingestão n8n");
      return;
    }
    toast("Arquivo enviado para indexação");
  };

  const indexDriveFile = async () => {
    if (!tenantId || !driveFileId || !n8nUrlDrive || !kbId) {
      toast("Preencha Tenant, KB, fileId e URL do n8n");
      return;
    }
    const payload = { file_id: driveFileId, tenant_id: tenantId, kb_id: kbId };
    const res = await fetch(n8nUrlDrive, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) {
      toast("Erro ao acionar ingestão do Drive");
      return;
    }
    toast("Arquivo do Drive enviado para indexação");
  };

  const connectGoogleDrive = async () => {
    const { error } = await supabase.auth.linkIdentity({ provider: "google" as any });
    if (error) {
      toast("Erro ao conectar Google Drive");
    } else {
      toast("Google Drive conectado");
    }
  };

  const saveWebhooks = () => {
    if (!tenantId) return;
    setSavingWebhooks(true);
    try {
      localStorage.setItem(`rag:n8n:files:${tenantId}`, n8nUrlFiles || "");
      localStorage.setItem(`rag:n8n:drive:${tenantId}`, n8nUrlDrive || "");
      toast("Webhooks salvos para o tenant");
    } finally {
      setSavingWebhooks(false);
    }
  };

  if (!canUse) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Nenhum tenant selecionado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle>RAG: Indexação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-3 max-w-xl">
            <Label>Base de Conhecimento</Label>
            <Select value={kbId} onValueChange={(v) => setKbId(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar Base de Conhecimento" />
              </SelectTrigger>
              <SelectContent>
                {kbList.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma base ativa encontrada</div>
                )}
                {kbList.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>{kb.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Tabs defaultValue="arquivos" className="space-y-4">
            <TabsList>
              <TabsTrigger value="arquivos">Arquivos</TabsTrigger>
              <TabsTrigger value="drive">Google Drive</TabsTrigger>
            </TabsList>
            <TabsContent value="arquivos" className="space-y-3">
              <Label>Webhook n8n (Arquivos)</Label>
              <Input placeholder="https://seu-n8n/webhook/rag-files" value={n8nUrlFiles} onChange={(e) => setN8nUrlFiles(e.target.value)} />
              <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <div className="flex gap-2">
                <Button onClick={uploadAndIndex} disabled={!kbId || !file || !n8nUrlFiles}>Enviar para Indexação</Button>
                <Button variant="outline" onClick={saveWebhooks} disabled={savingWebhooks || !tenantId}>Salvar Webhooks</Button>
              </div>
            </TabsContent>
            <TabsContent value="drive" className="space-y-3">
              <Label>Webhook n8n (Drive)</Label>
              <Input placeholder="https://seu-n8n/webhook/rag-drive" value={n8nUrlDrive} onChange={(e) => setN8nUrlDrive(e.target.value)} />
              <div className="flex gap-2">
                <Button onClick={connectGoogleDrive}>Conectar Google Drive</Button>
                <Button variant="outline" onClick={saveWebhooks} disabled={savingWebhooks || !tenantId}>Salvar Webhooks</Button>
              </div>
              <Input placeholder="Drive File ID" value={driveFileId} onChange={(e) => setDriveFileId(e.target.value)} />
              <Button onClick={indexDriveFile} disabled={!kbId || !driveFileId || !n8nUrlDrive}>Indexar do Drive</Button>
            </TabsContent>
          </Tabs>

          <Textarea placeholder="Observações" />
        </CardContent>
      </Card>
    </div>
  );
}
